import { useEffect, useState } from "react";
import {
  applyServiceWorkerUpdate,
  serviceWorkerUpdateReady,
  serviceWorkerUpdateReadyAt,
  subscribeServiceWorkerUpdate,
} from "../lib/service-worker";

// Reloading mid-diff costs a reviewer their scroll position and focus, so a pending update waits
// out the review unless it has been sitting there long enough to matter.
const reviewGraceMs = 60 * 60_000;
const recheckMs = 60_000;

export function reviewInProgress(pathname: string): boolean {
  return pathname.startsWith("/live/") || pathname.startsWith("/review/");
}

export function updateBannerVisible(options: {
  ready: boolean;
  readyAt: number | undefined;
  pathname: string;
  now: number;
}): boolean {
  if (!options.ready) return false;
  if (!reviewInProgress(options.pathname)) return true;
  return options.readyAt !== undefined && options.now - options.readyAt >= reviewGraceMs;
}

export function useServiceWorkerUpdate(): { visible: boolean; apply: () => void } {
  const [ready, setReady] = useState(serviceWorkerUpdateReady);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => subscribeServiceWorkerUpdate(() => setReady(true)), []);

  // Only ticks once an update is actually waiting; it exists so the banner can appear when the
  // reviewer leaves the review, or when the grace window expires.
  useEffect(() => {
    if (!ready) return;
    const timer = window.setInterval(() => setNow(Date.now()), recheckMs);
    return () => window.clearInterval(timer);
  }, [ready]);

  return {
    visible: updateBannerVisible({
      ready,
      readyAt: serviceWorkerUpdateReadyAt(),
      pathname: window.location.pathname,
      now,
    }),
    apply: applyServiceWorkerUpdate,
  };
}
